import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { PermissionService } from '../../../shared/services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../../../environments/environment';
import {
  TOKEN,
  LANG,
  USER_DATA,
  PERMISSIONS,
  ADMIN,
} from '../../../core/utils/constant';
import { debounceTime } from 'rxjs';
import { MenuService } from '../../../shared/services/menu.service';
import { TranslocoDirective } from '@jsverse/transloco';
import { IconsService, LoadingSpinnerComponent } from '@rieaya-core';
import { User } from '../../../core/interfaces/user';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
  imports: [TranslocoDirective, LoadingSpinnerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {
  icons = inject(IconsService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private menuService = inject(MenuService);
  private destroyRef = inject(DestroyRef);
  private permissionService = inject(PermissionService);
  private activatedRoute = inject(ActivatedRoute);

  userTypes!: any[];

  isLogin = signal<boolean>(false);
  loginLoading = signal<boolean>(false);
  loginError = signal<boolean>(false);
  roleLoading = signal<boolean>(false);
  selectedRoleIndex = signal<number | null>(null);

  ADMIN = ADMIN;

  userData!: User;

  constructor() {
    const token = this.activatedRoute.snapshot.queryParamMap.get('token') || '';

    if (token) {
      localStorage.setItem(TOKEN, token);
      this.loginLoading.set(true);
      this.loginError.set(false);
      this.authService
        .login()
        .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(500))
        .subscribe({
          next: (res: any) => {
            this.loginLoading.set(false);
            localStorage.setItem(TOKEN, res.data.token);
            const lang = localStorage.getItem(LANG) || 'ar';
            localStorage.setItem(LANG, lang);

            this.userData = res.data.userData;

            localStorage.setItem(
              USER_DATA,

              JSON.stringify(res.data.userData)
            );

            this.userTypes = res.data.rolesAndPermissions;
            this.isLogin.set(true);
          },
          error: () => {
            this.loginLoading.set(false);
            this.loginError.set(true);
            setTimeout(() => {
              window.location.href = environment.LoginSSOROP;
            }, 5000);
          },
        });
    }
  }

  selectUsertype(userType: any, index: number): void {
    if (this.roleLoading()) return;

    this.roleLoading.set(true);
    this.selectedRoleIndex.set(index);

    const userData = JSON.parse(localStorage.getItem(USER_DATA) ?? '');

    localStorage.setItem(
      USER_DATA,

      JSON.stringify({
        ...userData,
        role: userType.userType,
      })
    );

    localStorage.setItem(
      PERMISSIONS,
      JSON.stringify(this.flattenPermissions(userType.modules))
    );

    this.permissionService.refreshPermissions();

    const redirectUrl = this.findFirstAccessibleRoute(
      this.menuService.getStructure(userType.userType)
    );

    if (redirectUrl) {
      void this.router
        .navigate([redirectUrl])
        .then((navigated) => {
          if (!navigated) {
            this.roleLoading.set(false);
            this.selectedRoleIndex.set(null);
          }
        })
        .catch(() => {
          this.roleLoading.set(false);
          this.selectedRoleIndex.set(null);
        });
    } else {
      console.error('User has no accessible pages.');
      this.roleLoading.set(false);
      this.selectedRoleIndex.set(null);
      void this.router.navigate(['/']);
    }
  }

  private flattenPermissions(modules: any[]): string[] {
    const permissionIdSet = new Set<string>();

    for (const module of modules) {
      if (module.permissions) {
        for (const p of module.permissions) {
          permissionIdSet.add(p.permissionId);
        }
      }
    }

    return Array.from(permissionIdSet);
  }

  private findFirstAccessibleRoute(items: any[]): string | null {
    for (const item of items) {
      if (item.href && item.permissionId) {
        if (this.permissionService.hasAccess(item.permissionId)) {
          return item.href;
        }
      }

      if (item.children && item.children.length > 0) {
        const childUrl = this.findFirstAccessibleRoute(item.children);
        if (childUrl) {
          return childUrl;
        }
      }
    }

    return null;
  }
}
